import { ContentType, MedplumClient, encodeBase64, getReferenceString, resolveId } from "@medplum/core";
import axios from "axios";
import { API_ENDPOINTS } from "../api";
import epicLogo from "../assets/img/epicLogo.svg";
import epicPAtient from "../assets/img/EpicPatint.svg"
import AthenaPatient from "../assets/img/AthenaPatient.svg"
import cernepatient from "../assets/img/CernerPatient.svg"
import apple from "../assets/img/apple.svg"
import eclinical from "../assets/img/eclinical.svg"
import cernerLogo from "../assets/img/cerner.svg";
import googleFitLogo from "../assets/img/googleFitLogo.svg";
import dexcomLogo from "../assets/img/dexcomLogo.svg";
import fibitLogo from "../assets/img/fitBitLogo.svg";
import ouraLogo from "../assets/img/ouraLogo.svg";
import polarLogo from "../assets/img/polarLogo.svg"
import athenaLogo from "../assets/img/athenaHealthLogo.svg"
import nextGenLogo from "../assets/img/nextgen.svg"
import omronLogo from "../assets/img/omronLogo.png"
import withingLogo from "../assets/img/withingsLogo.png"
import stravaLogo from "../assets/img/stravaLogo.png"
import { updateProfile } from "../store/actions/ProfileActions";
import { Identifier } from "@medplum/fhirtypes";
import circularStarva from "../assets/img/starvaCircular.svg"
import circularDexcom from "../assets/img/dexcomCircular.svg"
import circularPolar from "../assets/img/PolarCircular.svg";
import circularWithings from "../assets/img/WithingsCircular.svg"
import garminLogo from "../assets/img/garmin-2.svg"
import ihealthlogo from "../assets/img/ihealth.png";
import circularEcw from "../assets/img/ecwpatient.svg"
import others from "../assets/img/others.svg";
import nextgenico from "../assets/img/nextgenico.svg";
import { useSelector } from "react-redux";
import { AppState } from "../store/types/appState";
import { getTokensAndUrl, ExtensionObject } from "../pages/HomePage.utils";
import { showNotification } from "@mantine/notifications";
import circularVeradigm from "../../src/assets/img/circularVeradigm.jpeg"
import veradigmLogo from "../../src/assets/img/vardigm.svg"






export function getProjectId(medplum: MedplumClient): string {
  return resolveId(medplum.getActiveLogin()?.project) as string;
}

export function cleanResource(resource: any): any {
  let meta = resource.meta;
  if (meta) {
    meta = {
      ...meta,
      lastUpdated: undefined,
      versionId: undefined,
      author: undefined,
    };
  }
  return {
    ...resource,
    meta,
  };
}

export function getPatient(resource: any): any {
  if (resource.resourceType === "Patient") {
    return resource;
  }
  if (
    resource.resourceType === "DiagnosticReport" ||
    resource.resourceType === "Encounter" ||
    resource.resourceType === "Observation" ||
    resource.resourceType === "ServiceRequest"
  ) {
    return resource.subject as any;
  }
  return undefined;
}

export function getSpecimen(resource: any): any | undefined {
  if (resource.resourceType === "Specimen") {
    return resource;
  }
  if (resource.resourceType === "Observation") {
    return resource.specimen;
  }
  if (
    resource.resourceType === "DiagnosticReport" ||
    resource.resourceType === "ServiceRequest"
  ) {
    return resource.specimen?.[0];
  }
  return undefined;
}

export function exportJsonFile(jsonString: string, fileName?: string): void {
  const blobForExport = new Blob([jsonString], { type: ContentType.JSON });
  const url = URL.createObjectURL(blobForExport);

  const link = document.createElement("a");
  link.href = url;

  const linkName = fileName ?? new Date().toISOString().replace(/\D/g, "");
  link.download = `${linkName}.json`;

  document.body.appendChild(link);
  link.click();

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

export const removeParam = (key: string, url: string): string => {
  try {
    return new URL(url, window.location.origin)
      .toString()
      .replace(new RegExp(`([?&])${key}=.*?(&|$)`), "$1");
  } catch (error) {
    console.error("Error parsing or modifying URL:", error);
    return url;
  }
};

export const getAccessToken = () => {
  try {
    const storedToken = JSON.parse(localStorage.getItem("activeLogin")!);
    if (storedToken) {
      return storedToken;
    }
    return null;
  } catch (error) {
    console.error("Error retrieving access token:", error);
    return null;
  }
};

export const getAndUploadGoogleFitData = async (
  authURL: string,
  id: string,
  redirectUri: string,
  patientId: string,
  secret: string,
  medplum: any
): Promise<boolean> => {
  const googleFitBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'google-fit-service'
  };
  const receiveTokenPayload = {
    type: "token",
    code_url: authURL,
    client_id: id,
    redirect_url: redirectUri,
    secret: secret,
    patient_id: patientId,
  };
  const googleToken = await medplum.executeBot(
    googleFitBot,
    receiveTokenPayload
  );
  if (googleToken.body) {
    let googleAuthToken = googleToken?.body;
    const tokenData = {
      token_data: googleAuthToken,
      patient_id: patientId,
      client_id: id,
      secret: secret,
    };
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "heart_beats",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "blood_pressure",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "blood_glucose",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "body_weight",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "height",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "spo2",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "body_fat",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "body_temperature",
    });
    await medplum.executeBot(googleFitBot, {
      ...tokenData,
      type: "steps_aggregate",
    });

    return true;
  }
  return false;
};

export interface Extension {
  url: string;
  extension: {
    url: string;
    valueString: string;
  }[];
}

export function getIntegrationTypes(
  extensions: Extension[] | undefined
): string[] {
  if (!extensions) {
    return [];
  }
  ;
  const integrationTypesSet = new Set<string>();

  extensions.forEach((extension) => {
    const integrationType = extension.extension.find(
      (item) => item.url === "integrationType"
    );
    if (integrationType) {
      integrationTypesSet.add(integrationType.valueString);
    }
  });
  return Array.from(integrationTypesSet);
}

export function getClientTypes(extensions: Extension[] | undefined): string[] {
  if (!extensions) {
    return [];
  }

  const integrationTypesSet = new Set<string>();

  extensions.forEach((extension) => {
    const integrationType = extension.extension.find(
      (item) => item.url === "clientType"
    );
    if (integrationType) {
      integrationTypesSet.add(integrationType.valueString);
    }
  });
  return Array.from(integrationTypesSet);
}

type PlatformLogo = {
  [key: string]: string;
};

export const platformLogo: PlatformLogo = {
  "epic": epicLogo,
  "cerner": cernerLogo,
  "athenahealth": athenaLogo,
  "nextgen": nextGenLogo,
  "googlefit": googleFitLogo,
  "dexcom": dexcomLogo,
  "fitbit": fibitLogo,
  "oura": ouraLogo,
  "polar": polarLogo,
  "omron": omronLogo,
  "withings": withingLogo,
  "strava": stravaLogo,
  "ecw": eclinical,
  "eclinicalworks": eclinical,
  "apple": apple,
  "epicPatient": epicPAtient,
  "cernerPatient": cernepatient,
  "athenaPatient": AthenaPatient,
  "garmin": garminLogo,
  "ihealth": ihealthlogo,
  "athenaone": athenaLogo,
  "veradigm":veradigmLogo

};
type circularPlatformLogo = {
  [key: string]: string;
};
export const circularPlatformLogo: circularPlatformLogo = {
  "epic": epicPAtient,
  "cerner": cernepatient,
  "athenahealth": AthenaPatient,
  "nextGen": nextgenico,
  "googlefit": googleFitLogo,
  "dexcom": circularDexcom,
  "fitbit": fibitLogo,
  "oura": ouraLogo,
  "polar": circularPolar,
  "omron": omronLogo,
  "withings": circularWithings,
  "strava": circularStarva,
  "ecw": circularEcw,
  "eclinicalworks": circularEcw,
  "apple": apple,
  "garmin": garminLogo,
  "ihealth": ihealthlogo,
  "others": others,
  'veradigm':circularVeradigm
}

export const getAndUploadDexcomData = async (
  authURL: string,
  id: string,
  redirectUri: string,
  patientId: string,
  secret: string,
  medplum: any
): Promise<boolean> => {
  const dexcomBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'dexcom-service'
  }
  const receiveTokenPayload = {
    type: "token",
    code_url: authURL,
    client_id: id,
    redirect_url: redirectUri,
    secret: secret,
    patient_id: patientId,
  };
  const dexcomToken = await medplum.executeBot(dexcomBot,
    receiveTokenPayload
  );
  if (dexcomToken.body) {
    let googleAuthToken = dexcomToken.body;
    const tokenData = {
      token_data: googleAuthToken,
      patient_id: patientId,
      client_id: id,
      secret: secret,
    };
    await medplum.executeBot(dexcomBot, {
      ...tokenData,
      type: "blood_glucose",
    });

    return true;
  }
  return false;
};

export const getAndUploadFitBitData = async (
  authURL: string,
  id: string,
  redirectUri: string,
  patientId: string,
  secret: string,
  medplum: any
): Promise<boolean> => {
  const receiveTokenPayload = {
    type: "token",
    code_url: authURL,
    client_id: id,
    redirect_url: redirectUri,
    secret: secret,
    patient_id: patientId,
  };
  const fitbitBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'fit-bit-service'
  };
  const fitBitToken = await medplum.executeBot(
    fitbitBot,
    receiveTokenPayload
  );
  if (fitBitToken.body) {
    let googleAuthToken = fitBitToken?.body;
    const tokenData = {
      token_data: googleAuthToken,
      patient_id: patientId,
      client_id: id,
      secret: secret,
    };
    await await medplum.executeBot(fitbitBot, {
      ...tokenData,
      type: "body",
    });
    return true;
  }
  return false;
};

export const getAndUploadOuraData = async (
  authURL: string,
  id: string,
  redirectUri: string,
  patientId: string,
  secret: string,
  medplum: any
): Promise<boolean> => {
  const receiveTokenPayload = {
    type: "token",
    code_url: authURL,
    client_id: id,
    redirect_url: redirectUri,
    secret: secret,
    patient_id: patientId,
  };
  const ouraBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'oura-service'
  };
  const ouraToken = await medplum.executeBot(
    ouraBot,
    receiveTokenPayload
  );
  if (ouraToken.body) {
    let googleAuthToken = ouraToken?.body;
    const tokenData = {
      "token_data": googleAuthToken,
      "patient_id": patientId,
      "client_id": id,
      "secret": secret
    }

    await await medplum.executeBot(ouraBot, { ...tokenData, type: "spo2" })
    await await medplum.executeBot(ouraBot, { ...tokenData, type: "heart_beats" })
    return true
  }
  return false
}

export const getAndUploadPolarData = async (authURL: string, id: string, redirectUri: string, patientId: string, secret: string, medplum: any): Promise<boolean> => {
  const polarBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'polar-service-new'
  }
  const receiveTokenPayload = {
    "type": "token",
    "code_url": authURL,
    "client_id": id,
    "redirect_url": redirectUri,
    "secret": secret,
    "patient_id": patientId
  }
  let polarToken = await medplum.executeBot(polarBot, receiveTokenPayload)
  if (polarToken.body) {
    let polarAuthToken = polarToken?.body;
    const tokenData = {
      "token_data": polarAuthToken,
      "patient_id": patientId,
      "client_id": id,
      "secret": secret
    }

    await await medplum.executeBot(polarBot, { ...tokenData, type: "spo2" })
    await await medplum.executeBot(polarBot, { ...tokenData, type: "body_temperature" })
    return true
  }

  return true
}

export const getAndUploadWithingsData = async (authURL: string, id: string, redirectUri: string, patientId: string, secret: string, medplum: any): Promise<boolean> => {
  const withingsBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'withing-service'
  }
  const receiveTokenPayload = {
    "type": "token",
    "code_url": authURL,
    "client_id": id,
    "redirect_url": redirectUri,
    "secret": secret,
    "patient_id": patientId
  }
  let withingsToken = await medplum.executeBot(withingsBot, receiveTokenPayload)
  if (withingsToken.body) {
    let polarAuthToken = withingsToken?.body;
    const tokenData = {
      "token_data": polarAuthToken,
      "patient_id": patientId,
      "client_id": id,
      "secret": secret
    }

    await await medplum.executeBot(withingsBot, { ...tokenData, type: "steps" })
    await await medplum.executeBot(withingsBot, { ...tokenData, type: "hr_max" })
    return true
  }

  return true
}

export const getAndUploadStravaData = async (authURL: string, id: string, redirectUri: string, patientId: string, secret: string, medplum: any): Promise<boolean> => {
  const stravaBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'strava-service'
  }
  const receiveTokenPayload = {
    "type": "token",
    "code_url": authURL,
    "client_id": id,
    "redirect_url": redirectUri,
    "secret": secret,
    "patient_id": patientId
  }
  let withingsToken = await medplum.executeBot(stravaBot, receiveTokenPayload)
  if (withingsToken.body) {
    let polarAuthToken = withingsToken?.body;
    const tokenData = {
      "token_data": polarAuthToken,
      "patient_id": patientId,
      "client_id": id,
      "secret": secret
    }

    await await medplum.executeBot(stravaBot, { ...tokenData, type: "heart_rate" })
    await await medplum.executeBot(stravaBot, { ...tokenData, type: "body_temperature" })
    return true
  }

  return true
}

export const getAndUploadIHealthData = async (code: string, id: string, redirectUri: string, patientId: string, secret: string, medplum: any): Promise<boolean> => {
  const iHealthTokenBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'iHealth-service'
  }
  const receiveTokenPayload = {
    "eventType": "token",
    "code": code,
    "clientId": id,
    "patientId": patientId
  }
  let iHealthToken = await medplum.executeBot(iHealthTokenBot, receiveTokenPayload)
  if (iHealthToken.body) {
    let iHealthAuthToken = iHealthToken?.body;
    const tokenData = {
      "tokenData": iHealthAuthToken,
      "patientId": patientId,
      "clientId": id,
    }

    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "heart_rate" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "blood_pressure" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "weight" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "blood_glucose" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "spo2" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "steps" })
    await await medplum.executeBot(iHealthTokenBot, { ...tokenData, eventType: "temperature" })

    return true
  }

  return true
}


export const getAndUploadGarminData = async (oauthData: any, id: string, verifier: string, patientId: string, secret: string, medplum: any): Promise<boolean> => {
  const garminBot = {
    system: 'https://api-stage.healthconnect.systems/bots',
    value: 'garmin-service'
  }
  const receiveTokenPayload = {
    "eventType": "token",
    "clientId": id,
    "patientId": patientId,
    "oauth": { ...oauthData, verifier }
  }

  let garminToken = await medplum.executeBot(garminBot, receiveTokenPayload)
  if (garminToken.body) {
    let garminAuthToken = garminToken?.body;
    const tokenData = {
      "clientId": id,
      "patientId": patientId,
      "oauth": garminAuthToken.oauth
    }

    await medplum.executeBot(garminBot, { ...tokenData, eventType: "dailySummary" })
    await medplum.executeBot(garminBot, { ...tokenData, eventType: "bodyComposition" })
    await medplum.executeBot(garminBot, { ...tokenData, eventType: "bloodPressure" })
    await medplum.executeBot(garminBot, { ...tokenData, eventType: "spo2" })
    return true
  }

  return true
}
interface Ext {
  url: string;
  valueString: string;
}

interface Meta {
  extension: {
    extension: Ext[];
  }[];
}

interface ClientApplication {
  meta: Meta;
  resourceType: string;
  name: string;
  secret: string;
  description?: string;
  redirectUri: string;
  id: string;
  identityProvider?: {
    userInfoUrl: string;
    clientId: string;
    clientSecret: string;
    authorizeUrl: string;
    tokenUrl: string;
  };
}

export function filterByClientType(
  data: ClientApplication[],
  clientTypeToFilter: string
): ClientApplication[] {
  return data.filter((item) =>
    item?.meta?.extension?.some((ext) =>
      ext.extension.some(
        (subExt) =>
          subExt.url === "clientType" &&
          subExt.valueString === clientTypeToFilter
      )
    )
  );
}

export async function storePatientDataUsingBot(
  medplum: MedplumClient,
  data: {
    epicClientId: string;
    epicAccessToken: string;
    storedPatientId: string;
    epicPatientId: string;
    accessToken: string;
  },
): Promise<any> {
  const body = {
    clientId: data.epicClientId,
    accessToken: data.epicAccessToken,
    patientId: data.storedPatientId,
    epicPatientId: data.epicPatientId,
  };
  const contentType = "application/json";
  const options = {
    headers: {
      Authorization: `Bearer ${data.accessToken}`,
    },
  };

  try {
    const response = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-service'
      },
      body,
      contentType,
      options
    );
    return response;
  } catch (error) {
    console.error("Error executing bot:", error);
    throw error;
  }
}

interface TokenData {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  refresh_token: string;
  __epic_dstu2_patient: string;
  id_token: string;
  patient: string;
}

interface ActiveLogin {
  accessToken: string;
}

export async function addAccessTokensToLoginedUser(
  medplum: MedplumClient,
  dispatch: any,
  data: TokenData,
  activeLogin: ActiveLogin,
  id: string,
  integrationType: string,
  name: string
) {
  let profile = await medplum.getProfileAsync(); // Do not trust this
  const newProfile = await medplum.readResource(profile?.resourceType === "Practitioner" ? "Practitioner" : "Patient", profile?.id ?? '');
  if (newProfile?.resourceType) profile = newProfile; // get updated Profile
  const storeTokensAsExtension = {
    url: `ClientApplication/${id}`,
    extension: [
      {
        url: "access_token",
        valueString: data?.access_token,
      },
      {
        url: "token_type",
        valueString: data?.token_type,
      },
      {
        url: "expires_in",
        valueString: data?.expires_in.toString(),
      },
      {
        url: "integrationType",
        valueString: integrationType?.toLowerCase(),
      },
      {
        url: "display",
        valueString: name,
      }
    ],
  };

  // Only add refresh token if it's available
  if (data.refresh_token) {
    storeTokensAsExtension.extension.push({
      url: "refresh_token",
      valueString: data.refresh_token,
    });
  }

  if (profile) {
    const mutableProfile = { ...profile };
    mutableProfile.extension = Array.isArray(mutableProfile.extension)
      ? [...mutableProfile.extension].filter((ext: any) => ext.url !== `ClientApplication/${id}`)
      : [];
    mutableProfile.extension.push(storeTokensAsExtension as any);
    profile = mutableProfile;
  }
  ;
  const updatedUser = await axios.put(
    `${API_ENDPOINTS.UPDATE_RESOURCE}/${profile?.id}`,
    profile,
    {
      headers: {
        Authorization: `Bearer ${activeLogin.accessToken}`,
      },
    }
  );
  if (updatedUser.status === 200) {
    dispatch(updateProfile(updatedUser.data.updatedResource));
  }
}

export async function cernerHandler(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'cerner-service'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

export async function athenaHandler(medplum: MedplumClient, payload: any, meta?: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'athena-service'
      }
      , payload);

    if (res?.statusCode < 300) return res;
    if (res?.body?.includes('Unauthorized') && meta) {
      const token = await getTokenResponse(
        medplum,
        meta.dispatch,
        meta.clientId,
        meta.integrationType,
        meta.refreshToken,
        meta.activeLogin,
        meta.name
      );
      if (token?.access_token) {
        const res = await medplum.executeBot(
          {
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'athena-service'
          },
          { ...payload, accessToken: token.access_token });

        return res;

      } else showNotification({
        color: "red",
        message: "Athena token is expired. Please connect Athena again.",
      });

    }

  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function athenaOneHandler(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'athenaone-service'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

export async function ecwHandler(medplum: MedplumClient, payload: any, meta?: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'ecw-service'
      }
      , payload);

    if (res?.statusCode < 300) return res;
    if (res?.body?.includes('Unauthorized') && meta) {
      const token = await getTokenResponse(
        medplum,
        meta.dispatch,
        meta.clientId,
        meta.integrationType,
        meta.refreshToken,
        meta.activeLogin,
        meta.name
      );
      if (token?.access_token) {
        const res = await medplum.executeBot(
          {
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'ecw-service'
          },
          { ...payload, accessToken: token.access_token });

        return res;

      } else showNotification({
        color: "red",
        message: "ECW token is expired. Please connect with ECW again.",
      });

    }
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

export async function epicHandler(medplum: MedplumClient, payload: any, meta?: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-auth-service'
      }
      , payload);

    if (res?.status > 300) throw new Error("Epic token expired. Please connect with Epic again.");
    return res;
    
  } catch (err) {
    if (meta) {
      const token = await getTokenResponse(
        medplum,
        meta.dispatch,
        meta.clientId,
        meta.integrationType,
        meta.refreshToken,
        meta.activeLogin,
        meta.name
      );  
      if (token?.access_token) {
        const res = await medplum.executeBot(
          {
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'epic-auth-service'
          },
          { ...payload, accessToken: token.access_token });

        return res;

      } else showNotification({
        color: "red",
        message: "Epic token is expired. Please connect with Epic again.",
      });

    }
    console.error("Unexpected error:", err);
  }
}
export async function pushToEpicHandler(medplum: MedplumClient, payload: any, meta?: any) {
  try {
    
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic'
      }
      , payload);

    if (res?.status > 300) throw new Error("Epic token expired. Please connect with Epic again.");
    return res;
    
  } catch (err) {
    if (meta) {
      const token = await getTokenResponse(
        medplum,
        meta.dispatch,
        meta.clientId,
        meta.integrationType,
        meta.refreshToken,
        meta.activeLogin,
        meta.name
      );  
      if (token?.access_token) {
        const res = await medplum.executeBot(
          {
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'epic'
          },
          { ...payload, oauthData: { accessToken: token.access_token} });

        return res;

      } else showNotification({
        color: "red",
        message: "Epic token is expired. Please connect with Epic again.",
      });

    }
    console.error("Unexpected error:", err);
  }
}

export async function kaleyraHandler(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'kaleyra-service'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function athenaComapabilitR4(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'Athena-capability-r4'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

export async function EpicComapabilitR4(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-capability-r4'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function EpicComapabilitDstu2(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-capability-dstu2'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function EpicComapabilitStu3(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-capability-stu3'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function cernerComapabilitR4(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'Cerner-capability-r4'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function cernerComapabilitdstu2(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'Cerner-capability-dstu2'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function ecwComapabilitr4(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'ecw-capability-r4'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

interface BotResource {
  fullUrl: string;
  resource: {
    name: string;
    id: string
  };
}

export function getObjectByName(name: string, profileState: BotResource[]): BotResource | null {
  if (!name || typeof name !== 'string') {
    console.error('Error: Name parameter must be a non-empty string.');
    return null;
  }

  if (!profileState || !Array.isArray(profileState)) {
    console.error('Error: Redux state must be provided as a non-empty array.');
    return null;
  }

  const lowerCaseName = name.trim().toLowerCase().replace(/\s/g, '');

  const foundBot = profileState.find(item => {
    if (item.resource && typeof item.resource.name === 'string') {
      const lowerCaseItemName = item.resource.name.trim().toLowerCase().replace(/\s/g, '');
      return lowerCaseItemName === lowerCaseName;
    }
    return false;
  });

  if (!foundBot) {
    console.warn(`Warning: No bot found with the name '${name}'.`);
    return null;
  }

  return foundBot;
}

interface ContactInfo<T> {
  id: string;
  extension?: { valueUrl: string; url: string }[];
  system: string;
  value: T;
  use: string;
}

export const extractValuesBySystem = <T>(contacts: ContactInfo<T>[], system: string): string => {
  return contacts
    .filter(contact => contact.system === system)
    .map(contact => contact.value).join('');
};

export const capitalizeString = (str: string): string => {
  if (!str) return str;

  return str.charAt(0).toUpperCase() + str.slice(1);
}

//Returns extension object based on integrationType
export function filterByValueString(data: Extension[], matchTo: string): Extension[] {
  const filteredObjects: Extension[] = [];
  for (const item of data) {
    for (const ext of item.extension) {
      if (ext.valueString === matchTo) {
        filteredObjects.push(item);
        break;
      }
    }
  }
  return filteredObjects;
}

//Return true if patient is from athena - checking patient identifier
export function includesAthenaHealth(data: any): boolean {
  for (const item of data) {
    if (typeof item.system === 'string' && item.system.includes('athenahealth')) {
      return true;
    }
  }
  return false;
}

export function filterIdentifier(identifiers: Identifier[], systemName: string): Identifier[] {
  return identifiers.filter(identifier => identifier.system == systemName);
}

export function calculateAge(dateOfBirth: Date): number {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}
export async function generateRefreshToken(clientId: string, clientSecret: string, refreshToken: string, provider: string): Promise<any> {

  let tokenEndpoint = ""
  if (provider === "epic") {
    tokenEndpoint = "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/token";
  }
  else if (provider === "cerner") {

    tokenEndpoint = "https://authorization.cerner.com/tenants/ec2458f2-1e24-41c8-b71b-0e701af7583d/protocols/oauth2/profiles/smart-v1/token";

  } else if (provider == "athenaHealth") {
    tokenEndpoint = "https://ap22sandbox.fhirapi.athenahealth.com/demoAPIServer/oauth2/token";

  }
  const headers = {
    "Authorization": `Basic ${encodeBase64(`${clientId}:${clientSecret}`)}`,
    "Content-Type": "application/x-www-form-urlencoded",

  };

  const postData = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  });
  try {
    const response = await axios.post(tokenEndpoint, postData, { headers });

    if (response.status === 200) {
      return response.data;
    } else {
      console.error(`Error: ${response.status} - ${response.data}`);
      return null;
    }
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
//return Appointment Date and time - May 1, 2024 4:30 PM to 5:00 PM
export function formatAppointmentDateTime(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const startOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  const endOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  const startFormatted = startDate.toLocaleString('en-US', startOptions);
  const endFormatted = endDate.toLocaleString('en-US', endOptions);

  return `${startFormatted} to ${endFormatted}`;
}

export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "booked":
      return "#F3ECFF";
      

    case "arrived":
      return "#D9EEFF";
    case "proposed":
      return "#B5B5B5";
    case "pending":
      return "#FFF9EB";
    case "fulfilled":
      return "#E7FFE5";
    case "cancelled":
      return "#FFF0F0";
    case "noshow":
      return "#EDEDED";
    case "checked-in":
      return "#F5FAFF";
    case "waitlist ":
      return "#DCDCDC";
    default:
      return "#B5B5B5";
  }
}
export const gettextcolor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "booked":
      return "#814ADB";

    case "arrived":
      return "#1B75BC";
    case "proposed":
      return "#292929";
    case "pending":
      return "#FF9F43";
    case "fulfilled":
      return "#00A300";
    case "cancelled":
      return "#F12222";
    case "noshow":
      return "#99A0AD";
    case "checked-in":
      return "#0570DE";
    case "waitlist ":
      return "#586171";

    default:
      return "#292929";
  }
};

export async function getTokenResponse(
  medplum: any,
  dispatch: any,
  clientId: any,
  integrationType: any,
  refreshToken: any,
  activeLogin: any,
  name: string
): Promise<any> {

  const refreshTokenPayload = {
    eventType: "refreshToken",
    clientId: clientId,
    refresh_token: refreshToken || ''
  };
  let tokenResponse;

  if (integrationType.toLowerCase() == "epic") {
    tokenResponse = await epicHandler(medplum, refreshTokenPayload);
  }
  else if (integrationType.toLowerCase() == "cerner") {
    tokenResponse = await cernerHandler(medplum, refreshTokenPayload);
    if (tokenResponse?.body && typeof tokenResponse.body === 'object') {
      tokenResponse.body.refresh_token = refreshToken;
    }
  }
  else if (integrationType.toLowerCase() == "athenahealth") {
    tokenResponse = await athenaHandler(medplum, refreshTokenPayload);
  }
  else if (["ecw", "eclinicalworks"].includes(integrationType.toLowerCase())) {
    tokenResponse = await ecwHandler(medplum, refreshTokenPayload);
  }

  const tokenData = tokenResponse?.body;
  if (tokenData?.access_token) {
    await addAccessTokensToLoginedUser(
      medplum,
      dispatch,
      tokenData,
      activeLogin,
      clientId,
      integrationType,
      name
    );
  }

  return tokenData || {};
}

export function checkUrl(url: string, targetString: string) {
  return url?.includes(targetString);
}

export async function searchAthenaPatient(medplum: MedplumClient, accessToken: string, name: string, meta?: any) {
  const payload = {
    eventType: "searchPatient",
    accessToken: accessToken,
    name: name,
  };
  return await athenaHandler(medplum, payload, meta);
}

export const calculateTimeDifference = (effectiveDateTime: string): string => {
  const now = new Date();
  const effectiveDate = new Date(effectiveDateTime);

  if (
    effectiveDateTime &&
    effectiveDate.toISOString().slice(0, 10) === effectiveDateTime
  ) {
    // Add time component if missing
    effectiveDate.setHours(0, 0, 0, 0);
  }

  const diffInSeconds = Math.floor(
    (now.getTime() - effectiveDate.getTime()) / 1000
  );

  if (diffInSeconds < 10) return "Just now";
  if (diffInSeconds < 60)
    return `${diffInSeconds} sec${diffInSeconds === 1 ? "" : "s"}`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60)
    return `${diffInMinutes} min`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"}`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"}`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return `${diffInWeeks} week${diffInWeeks === 1 ? "" : "s"}`;

  const diffInMonths =
    now.getMonth() -
    effectiveDate.getMonth() +
    12 * (now.getFullYear() - effectiveDate.getFullYear());
  if (diffInMonths < 12)
    return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"}`;

  const diffInYears = now.getFullYear() - effectiveDate.getFullYear();
  return `${diffInYears} year${diffInYears === 1 ? "" : "s"}`;
};


export function formatXml(xml: string): string {
  const PADDING = ' '.repeat(2); // Set desired indentation level
  const reg = /(>)(<)(\/*)/g;
  let formatted = '';
  let pad = 0;

  xml = xml.replace(reg, '$1\r\n$2$3');
  xml.split('\r\n').forEach((node) => {
    let indent = 0;
    if (node.match(/.+<\/\w[^>]*>$/)) {
      indent = 0;
    } else if (node.match(/^<\/\w/)) {
      if (pad !== 0) {
        pad -= 1;
      }
    } else if (node.match(/^<\w([^>]*[^\/])?>.*$/)) {
      indent = 1;
    } else {
      indent = 0;
    }

    formatted += PADDING.repeat(pad) + node + '\r\n';
    pad += indent;
  });

  return formatted.trim();
}
export function getProfilePicUrl(
  patientDetails: any
): string | undefined {
  const profilePicIdentifier = patientDetails.identifier.find(
    (id: any) => id.system === "profile-pic"
  );
  return profilePicIdentifier ? profilePicIdentifier.value : undefined;
}

export async function DocumentfetchForEpic(medplum: MedplumClient, payload: any) {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'epic-clinical-notes'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}
export async function createAndFetchDocument(accessToken: any, patientId: any, practitionerId: any, docName: any, docData: any, fileType: any, medplum: MedplumClient, medplumId: any) {
  const createApiURL = 'https://ap22sandbox.fhirapi.athenahealth.com/demoAPIServer/fhir/r4/DocumentReference';

  const data = {
    "resourceType": "DocumentReference",
    "subject": {
      "reference": `Patient/${patientId}`
    },
    "author": {
      "reference": `Practitioner/${practitionerId}`
    },
    "type": {
      "coding": [
        {
          "code": "51851-4",
          "display": "http://www.gehealthcare.com/fhir/cpsemr/identifiers/0/DocumentType"
        }
      ],
      "text": "http://www.gehealthcare.com/fhir/cpsemr/identifiers/0/DocumentType"
    },
    "created": "2018-10-30T10:01:04",
    "description": docName,
    "test": "sample document attachment",
    "content": {
      "attachment": {
        "name": docName,
        "description": docName,
        "title": docName,
        "code": "123",
        "contentType": fileType,
        "data": docData?.split(",")[1],
      }
    },
    "context": {
      "period": {
        "start": "2019-02-25T12:15:00"
      },
      "sourcePatientInfo": {
        "reference": `Patient/${patientId}`
      }
    }
  };

  try {
    // Create Document
    const createResponse = await fetch(createApiURL, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!createResponse.ok) {
      throw new Error(`Failed to create document: ${createResponse.statusText}`);
    }

    const locationHeader = createResponse.headers.get('Location');
    if (!locationHeader) {
      throw new Error('Location header is missing');
    }

    const documentId = locationHeader.split('/').pop();

    // Fetch Document
    const fetchApiURL = `https://ap22sandbox.fhirapi.athenahealth.com/demoAPIServer/fhir/r4/DocumentReference/${documentId}`;
    const fetchResponse = await fetch(fetchApiURL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/fhir+json'
      }
    });

    if (!fetchResponse.ok) {
      throw new Error(`Failed to fetch document: ${fetchResponse.statusText}`);
    }

    const documentData = await fetchResponse.json();


    const storedResource = await medplum.createResource(documentData);
    const binaryUrl = storedResource?.content?.[0]?.attachment?.url;
    const DocumentRefrenceId = storedResource?.id;
    if (!binaryUrl) {
      console.error('binary url not found')


    }

    const apiForBinaryUrl = `https://ap22sandbox.fhirapi.athenahealth.com/demoAPIServer/fhir/r4/${binaryUrl}`;
    let payloadForBinary
    let base64data
    let contentType

    const binaryResponse = await fetch(apiForBinaryUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': '*/*',
      }
    });


    if (!binaryResponse.ok) {
      throw new Error(`Failed to fetch binary URL: ${binaryResponse.statusText}`);
    } else {
      // Provide a default value for contentType in case it's null
      contentType = binaryResponse.headers.get('Content-Type') || '';

      const buffer = await binaryResponse.arrayBuffer();
      const blob = new Blob([buffer], { type: contentType });



      payloadForBinary = {
        "resourceType": "Binary",
        "contentType": contentType,
        data: docData?.split(",")[1],


      }

      const binaryMedplumData = medplum.createResource(payloadForBinary as any).then((Binary) => {
        const documenref = medplum.searchResources("DocumentReference", `_count=20&_id=${DocumentRefrenceId}&_offset=0&_sort=-_lastUpdated&_total=accurate`).then((response: any) => {
          const newPatientid = `Patient/${medplumId}`
          // response[0]?.meta?.compartment


          // response[0].meta.compartment[0].reference =newPatientid;

          response[0].subject.reference = newPatientid

          const newContent = [
            {
              attachment: {
                title: 'External Records',
                url: getReferenceString(Binary),
              },
            },
          ];
          response[0].content = newContent;
          const updateDocref = medplum.updateResource(response[0])



        });
      });





    }













    return binaryResponse;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}


